import { InputEventDetails } from "@/types/types";
import { CustomEventType } from "@/events/CustomEventRegistry";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BasicInputTextAreaStyles } from "../BasicInputTextareStyles";

@customElement("time-input")
class TimeInput extends LitElement {
	static readonly styles = [
		BasicInputTextAreaStyles,
		css`
			.time-input-container {
				display: flex;
				align-items: center;
				gap: 4px;
			}
			.time-input-group {
				display: flex;
				align-items: center;
				gap: 2px;
			}
			input {
				width: 50px;
				text-align: center;
			}
			.time-label {
				font-size: 12px;
				color: #666;
			}
		`
	];

	@property({ type: String }) id: string = "";
	@property({ type: String }) name: string = "";
	@property({ type: String }) label: string = "";
	@property({ type: String }) inputEventIdentifier: string = "";
	@property({ type: Boolean, reflect: true }) disabled: boolean = false;

	// Value in total seconds (what we receive and dispatch)
	@property({ type: Number }) value: number = 0;

	@state() private _minutes: number = 0;
	@state() private _seconds: number = 0;

	willUpdate(changedProperties: Map<string, unknown>) {
		if (changedProperties.has('value')) {
			this._convertSecondsToMinutesAndSeconds(this.value);
		}
	}

	private _convertSecondsToMinutesAndSeconds(totalSeconds: number): void {
		this._minutes = Math.floor(totalSeconds / 60);
		this._seconds = totalSeconds % 60;
	}

	private _convertToTotalSeconds(minutes: number, seconds: number): number {
		return (minutes * 60) + seconds;
	}

	private _handleMinutesChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		this._minutes = parseInt(input.value, 10) || 0;
		this._dispatchTimeChange();
	}

	private _handleSecondsChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		this._seconds = parseInt(input.value, 10) || 0;
		this._dispatchTimeChange();
	}

	private _dispatchTimeChange(): void {
		if (this.disabled) return;

		const totalSeconds = this._convertToTotalSeconds(this._minutes, this._seconds);

		this.dispatchEvent(new CustomEvent<InputEventDetails>(CustomEventType.TIME_INPUT_CHANGE, {
			detail: {
				additionalIdentifier: this.inputEventIdentifier,
				label: this.label,
				name: this.name,
				value: String(totalSeconds),
			},
			bubbles: true,
			composed: true
		}));
	}

	render() {
		return html`
			<div class="time-input-container">
				<div class="time-input-group">
					<input
						id="${this.id}-minutes"
						name="${this.name}-minutes"
						type="number"
						min="0"
						step="1"
						.value="${String(this._minutes)}"
						?disabled="${this.disabled}"
						@input="${this._handleMinutesChange}"
					/>
					<span class="time-label">m</span>
				</div>
				<div class="time-input-group">
					<input
						id="${this.id}-seconds"
						name="${this.name}-seconds"
						type="number"
						min="0"
						max="59"
						step="1"
						.value="${String(this._seconds)}"
						?disabled="${this.disabled}"
						@input="${this._handleSecondsChange}"
					/>
					<span class="time-label">s</span>
				</div>
			</div>
		`;
	}
}

export default TimeInput;